import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, House, User, Clock, ArrowRight, CalendarBlank, Bank, PencilSimple,
  Blueprint, Tag, Stack, Signature, FloppyDisk, IdentificationCard,
  Certificate,
} from '@phosphor-icons/react';
import { useAppStore } from '@/store/appStore';
import {
  useLoteHistorico, useModelos, useChangeModeloTramitado,
  useChangeCodigoApc, useChangePermisoConstruccion,
} from '@/hooks/useLotesKanban';
import { fmtDate, contrastingText } from '@/lib/utils';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import type { LoteKanban, LoteHistoricoEvento } from '@/types';
import type { ProyectoCode } from '@/types/sitePlan';
import type { UseMutationResult } from '@tanstack/react-query';

// Etiquetas amigables para los nombres de columna del audit log. Cualquier
// columna no listada se muestra con su nombre tecnico tal cual viene del
// trigger.
const COLUMNA_LABELS: Record<string, string> = {
  IDModDefecto: 'Modelo tramitado',
  IDModConstruido: 'Modelo construido',
  Frente: 'Frente',
  Fondo: 'Fondo',
  Area: 'Área',
  Area1NivelTramitado: 'Área 1er nivel',
  Area2NivelTramitado: 'Área 2do nivel',
  IDBloque: 'Bloque',
  IDProyecto: 'Proyecto',
  IDCreadorAPC: 'Creador APC',
  IDCasoVigente: 'Caso vigente',
  IDActividadC: 'Actividad',
  NumCatastro: 'Núm. catastro',
  Folio: 'Folio',
  Propietario: 'Propietario',
  PrecioLote: 'Precio lote',
  PresupuestoDirecto: 'Presupuesto directo',
  PresupuestoIndirecto: 'Presupuesto indirecto',
  CodigoAPC: 'Código APC',
  PermisoConstruccion: 'Permiso de construcción',
};

function labelColumna(col: string | null | undefined): string {
  if (!col) return '—';
  return COLUMNA_LABELS[col] ?? col;
}

function showValor(v: string | null | undefined): string {
  if (v == null || v === '') return '—';
  return v;
}

// Detalle del lote en modal: informacion del lote + timeline unificado
// (cambios de estado + cambios de campos auditados). Se monta mientras
// la tab Tramites esta activa.
export function LoteDetailModal({ proyecto }: { proyecto: ProyectoCode }) {
  const selectedLoteId = useAppStore(s => s.selectedLoteId);
  const selectLote = useAppStore(s => s.selectLote);
  const qc = useQueryClient();

  // Saco el lote del cache del Kanban (ya esta cargado y no hay endpoint
  // single-lote — el bulk del kanban tiene toda la info que muestra el modal).
  const lote = qc
    .getQueryData<LoteKanban[]>(['lotes-kanban', proyecto])
    ?.find(l => l.idLote === selectedLoteId) ?? null;

  // ESC cierra el modal (estilo TaskModal).
  useEffect(() => {
    if (selectedLoteId === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') selectLote(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedLoteId, selectLote]);

  const { data: historico = [], isLoading: loadingHist } = useLoteHistorico(selectedLoteId);

  if (selectedLoteId === null) return null;

  const titulo = lote?.idBd || lote?.lote || `Lote ${selectedLoteId}`;
  const estadoColor = lote?.colorEstadoActual ?? '#888780';
  const estadoTextColor = contrastingText(estadoColor);

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
      onClick={() => selectLote(null)}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <div className="flex items-center gap-2 min-w-0">
            <House size={18} weight="bold" className="text-stone-500 flex-shrink-0" />
            <h2 className="text-base font-semibold text-stone-900 truncate">
              {titulo}
            </h2>
            {lote?.estadoActual && (
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold flex-shrink-0"
                style={{ background: estadoColor, color: estadoTextColor }}
                title={lote.descripcionEstadoActual ?? undefined}
              >
                {lote.estadoActual}
              </span>
            )}
          </div>
          <button
            onClick={() => selectLote(null)}
            className="p-1 hover:bg-stone-100 rounded"
            aria-label="Cerrar"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {lote ? (
            <>
              {/* Descripcion del estado actual: bloque propio y prominente.
                  Es la informacion que mas pesa para entender en que esta
                  el lote concretamente, no solo el rotulo del estado. */}
              {lote.descripcionEstadoActual && (
                <div
                  className="rounded-lg px-3 py-2.5 text-sm leading-snug"
                  style={{
                    background: lote.colorEstadoActual
                      ? `${lote.colorEstadoActual}1A` // hex + ~10% alpha
                      : '#f1f5f9',
                    borderLeft: `3px solid ${lote.colorEstadoActual ?? '#94a3b8'}`,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold mb-0.5">
                    {lote.estadoActual ?? 'Estado'}
                  </div>
                  <div className="text-stone-800">{lote.descripcionEstadoActual}</div>
                </div>
              )}
              <LoteInfoBlock lote={lote} proyecto={proyecto} />
            </>
          ) : (
            <div className="text-sm text-stone-500 italic">
              Cargando datos del lote…
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-1.5">
              <Clock size={14} weight="bold" />
              Histórico de cambios
            </h3>
            {loadingHist ? (
              <div className="text-xs text-stone-500 italic">Cargando…</div>
            ) : historico.length === 0 ? (
              <div className="text-xs text-stone-500 italic px-3 py-4 bg-stone-50 rounded">
                Sin cambios registrados todavía. Cuando muevas el lote o se
                edite cualquier campo auditado, los cambios aparecerán aquí.
              </div>
            ) : (
              <Timeline eventos={historico} />
            )}
          </div>
        </div>

        {/* Footer minimal: la edicion se hace via drag-and-drop en el Kanban */}
        <div className="px-5 py-3 border-t border-stone-200 bg-stone-100 rounded-b-2xl flex items-center justify-end">
          <button
            onClick={() => selectLote(null)}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50 bg-white"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function LoteInfoBlock({ lote, proyecto }: { lote: LoteKanban; proyecto: ProyectoCode }) {
  // Modelo tramitado siempre se muestra como editable (icon Blueprint + lapiz).
  // Modelo reservado solo aparece como linea aparte cuando existe Y difiere
  // del tramitado — sino seria redundante.
  const reservado = lote.modeloReservado ?? null;
  const showReservadoAparte = !!reservado && reservado !== (lote.modeloTramitado ?? null);

  return (
    <div className="bg-stone-50 rounded-lg p-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <InfoLine label="Proyecto" value={lote.proyecto} />
        <InfoLine label="Bloque" value={lote.nomBloque} />
        <ModeloTramitadoEditable lote={lote} proyecto={proyecto} />
        {showReservadoAparte && (
          <InfoLine
            label="Modelo reservado"
            value={reservado}
            icon={<Tag size={11} weight="bold" />}
          />
        )}
        <TextFieldEditable
          lote={lote}
          proyecto={proyecto}
          label="Código APC"
          field="codigoApc"
          icon={<IdentificationCard size={11} weight="bold" />}
          placeholder="ej. 1282782"
          useHook={useChangeCodigoApc}
        />
        <TextFieldEditable
          lote={lote}
          proyecto={proyecto}
          label="Permiso construcción"
          field="permisoConstruccion"
          icon={<Certificate size={11} weight="bold" />}
          placeholder="ej. PC 221-2024"
          useHook={useChangePermisoConstruccion}
        />
        <InfoLine label="Cliente" value={lote.cliente} />
        <InfoLine label="Niveles" value={lote.niveles} />
        <InfoLine
          label="Banco"
          value={lote.banco}
          icon={lote.banco ? <Bank size={11} weight="bold" /> : undefined}
        />
        {lote.vendedor && <InfoLine label="Vendedor" value={lote.vendedor} />}
        {lote.formalizador && (
          <InfoLine
            label="Formalizador"
            value={lote.formalizador}
            icon={<Signature size={11} weight="bold" />}
          />
        )}
        {lote.fechaReserva && (
          <InfoLine
            label="Fecha reserva"
            value={fmtDate(lote.fechaReserva)}
            icon={<CalendarBlank size={11} weight="bold" />}
          />
        )}
        {lote.nomCreadorApc && (
          <InfoLine
            label="Creador APC"
            value={lote.nomCreadorApc}
            icon={<User size={11} weight="bold" />}
          />
        )}
        {lote.nomFase && (
          <InfoLine
            label="Fase"
            value={lote.nomFase}
            icon={<Stack size={11} weight="bold" />}
          />
        )}
        {lote.etapaActual && <InfoLine label="Etapa actual" value={lote.etapaActual} />}
        {lote.diasEnEstado != null && (
          <InfoLine
            label="Días en estado"
            value={`${lote.diasEnEstado}${lote.diasProyectados ? ` / ${lote.diasProyectados}` : ''} d`}
            icon={<Clock size={11} weight="bold" />}
          />
        )}
      </div>
    </div>
  );
}

function InfoLine({
  label, value, icon,
}: {
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-[11px] font-medium text-stone-500 flex-shrink-0">
        {label}
      </span>
      <span className="text-stone-800 truncate flex items-center gap-1">
        {icon}
        {value || <span className="text-stone-400 italic">—</span>}
      </span>
    </div>
  );
}

// Edicion inline del Modelo Tramitado. View mode: linea normal con lapiz
// hover-revealed. Edit mode: ocupa col-span-2 y muestra SearchableSelect
// con el catalogo filtrado por proyecto + botones Cancelar / Guardar.
// Optimistic update via useChangeModeloTramitado (en el cache del kanban).
function ModeloTramitadoEditable({
  lote, proyecto,
}: {
  lote: LoteKanban;
  proyecto: ProyectoCode;
}) {
  const [editing, setEditing] = useState(false);
  const [pickerValue, setPickerValue] = useState<number | null>(lote.idModDefecto ?? null);

  // Solo cargamos el catalogo cuando se entra a edit (evita el fetch al
  // abrir el modal sin necesidad).
  const { data: modelos = [], isLoading } = useModelos(editing ? proyecto : null);
  const mutation = useChangeModeloTramitado(proyecto);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setPickerValue(lote.idModDefecto ?? null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setPickerValue(lote.idModDefecto ?? null);
  }

  async function save() {
    const next = pickerValue ?? null;
    // No-op si no cambio nada.
    if (next === (lote.idModDefecto ?? null)) {
      setEditing(false);
      return;
    }
    const nombreOptimista = next == null
      ? null
      : (modelos.find(m => m.idModelo === next)?.nombre ?? null);
    try {
      await mutation.mutateAsync({
        idLote: lote.idLote,
        idModDefecto: next,
        nombreOptimista,
      });
      setEditing(false);
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo guardar el modelo tramitado');
    }
  }

  if (!editing) {
    // View mode: la linea es como InfoLine pero con el lapiz pegado.
    return (
      <div className="flex items-baseline gap-1.5 min-w-0 group">
        <span className="text-[11px] font-medium text-stone-500 flex-shrink-0">
          Modelo tramitado
        </span>
        <span className="text-stone-800 truncate flex items-center gap-1 min-w-0">
          <Blueprint size={11} weight="bold" />
          {lote.modeloTramitado || <span className="text-stone-400 italic">—</span>}
        </span>
        <button
          type="button"
          onClick={startEdit}
          className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-stone-700 transition-opacity p-0.5 rounded hover:bg-stone-200 flex-shrink-0"
          title="Editar modelo tramitado"
          aria-label="Editar modelo tramitado"
        >
          <PencilSimple size={11} weight="bold" />
        </button>
      </div>
    );
  }

  // Edit mode: spans both columns, picker + botones.
  const options = modelos.map(m => ({
    value: m.idModelo,
    label: m.nombre || `Modelo ${m.idModelo}`,
  }));

  return (
    <div className="col-span-2 flex items-center gap-2">
      <span className="text-[11px] font-medium text-stone-500 flex-shrink-0 flex items-center gap-1">
        <Blueprint size={11} weight="bold" />
        Modelo tramitado
      </span>
      <div className="flex-1 min-w-0">
        <SearchableSelect<number>
          options={options}
          value={pickerValue}
          onChange={setPickerValue}
          placeholder={isLoading ? 'Cargando modelos…' : '— Sin modelo —'}
          disabled={isLoading || mutation.isPending}
        />
      </div>
      <button
        type="button"
        onClick={cancel}
        disabled={mutation.isPending}
        className="text-xs px-2 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={save}
        disabled={mutation.isPending || isLoading}
        className="text-xs px-2 py-1 bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 flex items-center gap-1"
      >
        <FloppyDisk size={11} weight="bold" />
        {mutation.isPending ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  );
}

// Editor inline generico para campos de texto del lote (CodigoAPC,
// PermisoConstruccion). Misma estructura visual que ModeloTramitadoEditable:
// view mode con lapiz hover-revealed, edit mode col-span-2 con input +
// botones Cancelar/Guardar. Pasa la mutation por prop para no acoplarlo
// a un hook especifico.
//
// Enter guarda; Escape cancela; trim de strings vacios -> null (libera la
// columna). focus se delega al input via autoFocus + useRef.
type LoteTextField = 'codigoApc' | 'permisoConstruccion';
type LoteTextFieldMutation = UseMutationResult<
  LoteKanban,
  Error,
  { idLote: number; value: string | null }
>;

function TextFieldEditable({
  lote, proyecto, label, field, icon, placeholder, useHook,
}: {
  lote: LoteKanban;
  proyecto: ProyectoCode;
  label: string;
  field: LoteTextField;
  icon: React.ReactNode;
  placeholder?: string;
  useHook: (p: ProyectoCode | null) => LoteTextFieldMutation;
}) {
  const current = lote[field] ?? null;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(current ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const mutation = useHook(proyecto);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setText(current ?? '');
    setEditing(true);
    // Foco diferido para que el input ya este en el DOM cuando enfocamos.
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function cancel() {
    setEditing(false);
    setText(current ?? '');
  }

  async function save() {
    const next = text.trim() === '' ? null : text.trim();
    if (next === current) {
      setEditing(false);
      return;
    }
    try {
      await mutation.mutateAsync({ idLote: lote.idLote, value: next });
      setEditing(false);
    } catch (err: any) {
      window.alert(err?.message || `No se pudo guardar ${label}`);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-baseline gap-1.5 min-w-0 group">
        <span className="text-[11px] font-medium text-stone-500 flex-shrink-0">
          {label}
        </span>
        <span className="text-stone-800 truncate flex items-center gap-1 min-w-0">
          {icon}
          {current || <span className="text-stone-400 italic">—</span>}
        </span>
        <button
          type="button"
          onClick={startEdit}
          className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-stone-700 transition-opacity p-0.5 rounded hover:bg-stone-200 flex-shrink-0"
          title={`Editar ${label.toLowerCase()}`}
          aria-label={`Editar ${label.toLowerCase()}`}
        >
          <PencilSimple size={11} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="col-span-2 flex items-center gap-2">
      <span className="text-[11px] font-medium text-stone-500 flex-shrink-0 flex items-center gap-1">
        {icon}
        {label}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); void save(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        placeholder={placeholder}
        disabled={mutation.isPending}
        maxLength={50}
        className="flex-1 min-w-0 px-2 py-1 text-sm border border-stone-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-green-100 disabled:opacity-50"
      />
      <button
        type="button"
        onClick={cancel}
        disabled={mutation.isPending}
        className="text-xs px-2 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={save}
        disabled={mutation.isPending}
        className="text-xs px-2 py-1 bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 flex items-center gap-1"
      >
        <FloppyDisk size={11} weight="bold" />
        {mutation.isPending ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  );
}

// Timeline unificado: cada fila puede ser un cambio de estado (tipo='estado')
// o de campo (tipo='campo'). El renderer dispatch-ea por tipo y mantiene la
// linea vertical + circulo de color para los dos casos (color del estado
// nuevo, o gris para cambios de campo).
function Timeline({ eventos }: { eventos: LoteHistoricoEvento[] }) {
  return (
    <ol className="relative pl-5">
      <span
        className="absolute left-[7px] top-0 bottom-0 w-px bg-stone-200"
        aria-hidden
      />
      {eventos.map((e, i) => (
        <TimelineRow
          key={`${e.tipo}-${e.fechaCambio}-${i}`}
          evento={e}
          isLast={i === eventos.length - 1}
        />
      ))}
    </ol>
  );
}

function TimelineRow({ evento, isLast }: { evento: LoteHistoricoEvento; isLast: boolean }) {
  if (evento.tipo === 'estado') {
    const color = evento.colorEstadoNuevo || '#888780';
    return (
      <li className="relative pb-3 last:pb-0">
        <span
          className="absolute -left-5 top-1 w-3 h-3 rounded-full border-2 border-white"
          style={{ background: color }}
          aria-hidden
        />
        <div className="text-xs space-y-0.5">
          <div className="flex items-center gap-1.5 flex-wrap text-stone-700">
            {evento.estadoAnterior ? (
              <>
                <span className="text-stone-500 truncate">{evento.estadoAnterior}</span>
                <ArrowRight size={10} weight="bold" className="text-stone-400 flex-shrink-0" />
              </>
            ) : (
              <span className="text-stone-400 italic text-[10px]">(inicial)</span>
            )}
            <span className="font-semibold text-stone-900 truncate">
              {evento.estadoNuevo ?? `Estado #${evento.idEstadoLoteNuevo ?? '?'}`}
            </span>
            {evento.nomFaseNueva && (
              <span className="text-[10px] text-stone-400">· {evento.nomFaseNueva}</span>
            )}
            {!evento.nomFaseNueva && evento.etapaNuevo && (
              <span className="text-[10px] text-stone-400">· {evento.etapaNuevo}</span>
            )}
          </div>
          {/* Descripcion del estado nuevo: la info que mas importa para
              entender que fue exactamente el cambio. Se muestra siempre
              que el estado tenga descripcion. */}
          {evento.descripcionEstadoNuevo && (
            <div className="text-[11px] text-stone-600 leading-snug">
              {evento.descripcionEstadoNuevo}
            </div>
          )}
          <MetaLine evento={evento} hideDias={isLast} />
        </div>
      </li>
    );
  }

  // tipo === 'campo'
  return (
    <li className="relative pb-3 last:pb-0">
      <span
        className="absolute -left-5 top-1 w-3 h-3 rounded-full border-2 border-white bg-stone-400 flex items-center justify-center"
        aria-hidden
      >
        <PencilSimple size={6} weight="bold" className="text-white" />
      </span>
      <div className="text-xs space-y-0.5">
        <div className="flex items-center gap-1.5 flex-wrap text-stone-700">
          <span className="font-semibold text-stone-900">{labelColumna(evento.columna)}</span>
          <span className="text-stone-500 truncate">{showValor(evento.valorAnterior)}</span>
          <ArrowRight size={10} weight="bold" className="text-stone-400 flex-shrink-0" />
          <span className="text-stone-800 truncate">{showValor(evento.valorNuevo)}</span>
        </div>
        <MetaLine evento={evento} hideDias />
      </div>
    </li>
  );
}

function MetaLine({ evento, hideDias }: { evento: LoteHistoricoEvento; hideDias: boolean }) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-stone-500">
      <span className="flex items-center gap-1">
        <CalendarBlank size={10} weight="bold" />
        {fmtDate(evento.fechaCambio, true)}
      </span>
      {evento.nomModificador && (
        <span className="flex items-center gap-1">
          <User size={10} weight="bold" />
          {evento.nomModificador}
        </span>
      )}
      {!hideDias && evento.diasEnEstadoAnterior != null && (
        <span className="text-stone-400 italic">
          tras {evento.diasEnEstadoAnterior}d en el estado anterior
        </span>
      )}
    </div>
  );
}
